import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, User as UserIcon } from 'lucide-react';
import { API_ENDPOINTS, buildIsConsumerUrl } from '../../config/api.config'; // Added buildIsConsumerUrl import
import { useModal } from '../../components/layout';
import {
    User,
    Group,
    UserRole,
    APIUser
} from "../../types/auth";
import { Badge, Card, Form } from 'react-bootstrap';
import UserModal from '../../components/modals/user/UserModal';
import '../../../scss/custom.scss';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import ContentSkeleton from "@/components/loading/ContentSkeleton.tsx";

interface UserFormData {
    email: string;
    fullName: string;
    groupIds: string[];
    status: 'active' | 'inactive';
}

interface ConsumerOrgResponse {
    isConsumer: boolean;
}

interface PaginationData {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

interface TransformAPIUserParams {
    apiUser: APIUser;
    currentUserContext: User;
    availableGroups: Group[];
}

interface CreateUserData {
    email: string;
    fullName: string;
    groupIds: string[];
    status: 'active' | 'inactive';
    role: 'user' | 'admin' | 'rag_user';
}

interface APIResponse<T> {
    data: T[];
    pagination: PaginationData;
}

const ITEMS_PER_PAGE = 10;

const initialFormData: UserFormData = {
    email: '',
    fullName: '',
    groupIds: [],
    status: 'active'
};

const UsersPage: React.FC = () => {
    // Updated auth usage
    const { user: currentUser, isLoading: authLoading } = useAuth();
    const initializationRef = useRef(false);
    const modalState = useModal();
    const [users, setUsers] = useState<User[]>([]);
    const [groups, setGroups] = useState<Group[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [formData, setFormData] = useState<UserFormData>(initialFormData);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const searchTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
    const [emailError, setEmailError] = useState<string | undefined>();
    const [pagination, setPagination] = useState<PaginationData>({
        page: 1,
        limit: ITEMS_PER_PAGE,
        total: 0,
        totalPages: 0
    });
    const [initialFormState, setInitialFormState] = useState<UserFormData>(initialFormData);

    // Keep your transformAPIUser function exactly the same
    const transformAPIUser = ({
                                  apiUser,
                                  currentUserContext,
                                  availableGroups
                              }: TransformAPIUserParams): User => {
        console.log('Transforming API user:', {
            apiUserDetails: {
                id: apiUser.id,
                email: apiUser.email,
                role: apiUser.role,
                groups: apiUser.groups,
                organization_id: apiUser.organization_id
            },
            currentContext: {
                organizationId: currentUserContext.organizationId,
                organizationDomain: currentUserContext.organizationDomain
            },
            availableGroupCount: availableGroups.length
        });

        const userGroups = (Array.isArray(apiUser.groups) ? apiUser.groups : []).map((groupId: string) => {
            const foundGroup = availableGroups.find(g => g.id === groupId);
            return {
                id: groupId,
                name: foundGroup?.name || 'Loading...',
                is_admin_group: foundGroup?.is_admin_group || false
            };
        });

        const transformedUser: User = {
            id: apiUser.id,
            email: apiUser.email,
            full_name: apiUser.full_name,
            fullName: apiUser.full_name || '',
            status: apiUser.status || 'active',
            role: apiUser.role as UserRole,
            organizationId: apiUser.organization_id || currentUserContext.organizationId,
            organizationDomain: currentUserContext.organizationDomain,
            isSystemOrg: apiUser.is_system_org || false,
            groups: userGroups,
            auth_providers: apiUser.auth_providers || [],
            force_password_change: apiUser.force_password_change || false
        };

        return transformedUser;
    };

    useEffect(() => {
        const initialize = async () => {
            if (initializationRef.current || !currentUser?.id) return;

            try {
                setIsLoading(true);
                setError(null);

                // Fetch groups and users data in parallel
                const [groupsResult, usersResult] = await Promise.all([
                    apiService.get<{ data: Group[] }>(
                        API_ENDPOINTS.GROUPS.LIST + '?all=true'
                    ),
                    apiService.get<APIResponse<APIUser>>(
                        `${API_ENDPOINTS.USERS.LIST}?page=1&limit=${ITEMS_PER_PAGE}`
                    )
                ]);

                setGroups(groupsResult.data || []);

                const transformedUsers = usersResult.data.map(apiUser =>
                    transformAPIUser({
                        apiUser,
                        currentUserContext: currentUser,
                        availableGroups: groupsResult.data || []
                    })
                );

                setUsers(transformedUsers);
                setPagination(usersResult.pagination);
                initializationRef.current = true;

            } catch (error) {
                console.error('Initialization error:', error);
                const errorMessage = error instanceof Error ? error.message : 'Failed to initialize';
                setError(errorMessage);
                modalState.setError(errorMessage);
                initializationRef.current = false;
            } finally {
                setIsLoading(false);
            }
        };

        initialize();

        return () => {
            initializationRef.current = false;
            setIsLoading(false);
            setError(null);
        };
    }, [currentUser?.id]);

    // Keep all your existing handler functions exactly the same
    const fetchUsers = async (page: number, search?: string): Promise<void> => {
        if (!currentUser?.organizationId || !initializationRef.current) {
            console.warn('Cannot fetch users:', {
                hasOrgId: !!currentUser?.organizationId,
                isInitialized: initializationRef.current
            });
            return;
        }

        try {
            setIsLoading(true);
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: ITEMS_PER_PAGE.toString(),
                ...(search ? { q: search } : {})
            });

            const result = await apiService.get<APIResponse<APIUser>>(
                `${API_ENDPOINTS.USERS.LIST}?${queryParams}`
            );

            const transformedUsers = result.data.map(apiUser =>
                transformAPIUser({
                    apiUser,
                    currentUserContext: currentUser,
                    availableGroups: groups
                })
            );

            setUsers(transformedUsers);
            setPagination(result.pagination);
        } catch (error) {
            console.error('Error fetching users:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to fetch users';
            setError(errorMessage);
            modalState.setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = (value: string) => {
        setSearchQuery(value);
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }
        setError(null);
        searchTimeout.current = setTimeout(() => {
            setCurrentPage(1);
            fetchUsers(1, value.trim());
        }, 300);
    };

    const handleOpenCreateModal = (): void => {
        setSelectedUser(null);
        setFormData(initialFormData);
        setInitialFormState({...initialFormData}); // Store a copy of the initial state
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (user: User): void => {
        const newFormData = {
            email: user.email,
            fullName: user.fullName,
            groupIds: user.groups?.map(group => group.id) || [],
            status: user.status || 'active'
        };

        setSelectedUser(user);
        setFormData(newFormData);
        setInitialFormState({...newFormData}); // Store a copy of the initial state
        setIsModalOpen(true);
    };

    const handleCloseModal = (force: boolean = false): void => {
        // If force is false and there are unsaved changes, ask for confirmation
        if (!force && JSON.stringify(initialFormState) !== JSON.stringify(formData)) {
            if (!window.confirm('You have unsaved changes. Are you sure you want to close the modal?')) {
                return; // Don't close if user cancels
            }
        }

        setIsModalOpen(false);
        setSelectedUser(null);
        setFormData(initialFormData);
        setEmailError(undefined);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
        fetchUsers(newPage, searchQuery);
    };

    const handleSubmit = async (): Promise<void> => {
        console.log("🔍 Submit button clicked, form data:", formData);

        if (!currentUser?.organizationId) {
            console.error("No organization ID found");
            setEmailError('Organization ID not found');
            return;
        }

        modalState.setIsLoading(true);
        setEmailError(undefined);

        try {
            if (!formData.email?.trim()) {
                setEmailError('Email is required');
                modalState.setIsLoading(false);
                return;
            }

            if (!formData.fullName?.trim()) {
                setEmailError('Full name is required');
                modalState.setIsLoading(false);
                return;
            }

            // Check if organization is a consumer org
            try {
                const response = await apiService.get<ConsumerOrgResponse>(buildIsConsumerUrl(currentUser.organizationId));
                console.log("Consumer org check response:", response); // Debug log

                const isConsumer = response.isConsumer;
                console.log("Is consumer org:", isConsumer); // Debug log

                // Only validate email domain if not a consumer org
                if (!isConsumer) {
                    if (!currentUser?.email) {
                        console.error("Admin email is missing from currentUser:", currentUser);
                        setEmailError('Unable to determine organization domain. Please contact support.');
                        modalState.setIsLoading(false);
                        return;
                    }

                    const adminEmailParts = currentUser.email.split('@');
                    const organizationDomain = adminEmailParts[1];

                    if (!organizationDomain) {
                        console.error("Failed to extract organization domain from admin email:", currentUser.email);
                        setEmailError('Unable to determine organization domain. Please contact support.');
                        modalState.setIsLoading(false);
                        return;
                    }

                    console.log("🔍 Using organization domain:", organizationDomain);

                    const userEmailParts = formData.email.split('@');
                    const userEmailDomain = userEmailParts[1];

                    if (!userEmailDomain || userEmailDomain.toLowerCase() !== organizationDomain.toLowerCase()) {
                        setEmailError(`Email must be from the ${organizationDomain} domain`);
                        modalState.setIsLoading(false);
                        return;
                    }
                } else {
                    console.log("Consumer org - skipping domain validation");
                }

                // Proceed with user creation/update
                if (!selectedUser) {
                    const createData: CreateUserData = {
                        email: formData.email.trim(),
                        fullName: formData.fullName.trim(),
                        groupIds: formData.groupIds || [],
                        status: formData.status || 'active',
                        role: 'user'
                    };

                    try {
                        await apiService.post(API_ENDPOINTS.USERS.INVITE, createData);
                        console.log("✅ Invitation successful");
                        await fetchUsers(currentPage, searchQuery);
                        handleCloseModal();
                        modalState.setSuccess('Invitation sent successfully');
                    } catch (apiError: any) {
                        console.error("❌ API Error details:", apiError);
                        setEmailError(apiError.data?.details || apiError.data?.error || `Failed to send invitation: ${apiError.message}`);
                        throw apiError;
                    }
                } else {
                    const updateData = {
                        email: formData.email.trim(),
                        fullName: formData.fullName.trim(),
                        status: formData.status || 'active',
                        groupIds: formData.groupIds || []
                    };

                    try {
                        await apiService.put(
                            `${API_ENDPOINTS.USERS.UPDATE}/${selectedUser.id}`,
                            updateData
                        );
                        console.log("✅ User update successful");
                        modalState.setSuccess('User updated successfully');
                        await fetchUsers(currentPage, searchQuery);
                        handleCloseModal(true);
                    } catch (apiError: any) {
                        console.error("❌ API Error during update:", apiError);
                        setEmailError(apiError.data?.details || apiError.data?.error || `Failed to update user: ${apiError.message}`);
                        throw apiError;
                    }
                }
            } catch (error) {
                console.error("Error checking consumer organization status:", error);
                setEmailError('Error validating organization type. Please try again.');
                modalState.setIsLoading(false);
                return;
            }
        } catch (error) {
            console.error('Submit Error:', error);
        } finally {
            modalState.setIsLoading(false);
        }
    };

    const handleDeleteUser = async (userId: string): Promise<void> => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;

        try {
            modalState.setIsLoading(true);
            await apiService.delete(`${API_ENDPOINTS.USERS.DELETE}/${userId}`);
            await fetchUsers(currentPage, searchQuery);
            modalState.setSuccess('User deleted successfully');
        } catch (error) {
            console.error('Error deleting user:', error);
            modalState.setError(error instanceof Error ? error.message : 'Failed to delete user');
        } finally {
            modalState.setIsLoading(false);
        }
    };

    const renderPagination = () => {
        const maxVisiblePages = 5;
        let calculatedStartPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(pagination.totalPages, calculatedStartPage + maxVisiblePages - 1);

        if (endPage - calculatedStartPage + 1 < maxVisiblePages) {
            calculatedStartPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        const pages = [];
        for (let i = calculatedStartPage; i <= endPage; i++) {
            pages.push(
                <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                    <button
                        className="page-link"
                        onClick={() => handlePageChange(i)}
                    >
                        {i}
                    </button>
                </li>
            );
        }

        return (
            <nav aria-label="Users pagination" className="mt-3">
                <ul className="pagination mb-0 justify-content-end">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                    </li>
                    {calculatedStartPage > 1 && (
                        <>
                            <li className="page-item">
                                <button className="page-link" onClick={() => handlePageChange(1)}>1</button>
                            </li>
                            {calculatedStartPage > 2 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                        </>
                    )}
                    {pages}
                    {endPage < pagination.totalPages && (
                        <>
                            {endPage < pagination.totalPages - 1 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                            <li className="page-item">
                                <button
                                    className="page-link"
                                    onClick={() => handlePageChange(pagination.totalPages)}
                                >
                                    {pagination.totalPages}
                                </button>
                            </li>
                        </>
                    )}
                    <li className={`page-item ${currentPage === pagination.totalPages ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === pagination.totalPages}
                        >
                            Next
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    if (authLoading || !currentUser) return null;

    return (
        <>
            <div className="page-container">
                <div className="mb-4">
                    <h5 className="mb-3">Users</h5>
                    {isLoading ? (
                        <ContentSkeleton />
                    ) : (
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body className="p-4">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <div className="flex-grow-1">
                                    <div className="mt-3">
                                        <Form.Group>
                                            <div className="d-flex gap-2">
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Search users..."
                                                    value={searchQuery}
                                                    onChange={(e) => handleSearch(e.target.value)}
                                                    className="border"
                                                    style={{height: '38px'}}
                                                />
                                                <button
                                                    className="btn btn-outline-primary"
                                                    onClick={handleOpenCreateModal}
                                                    style={{height: '38px', whiteSpace: 'nowrap'}}
                                                >
                                                    <Plus size={16} className="me-1"/>
                                                    New User
                                                </button>
                                            </div>
                                        </Form.Group>
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-8">
                                    {isLoading ? (
                                        <div className="text-center p-2">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    ) : error ? (
                                        <div className="alert alert-danger" role="alert">
                                            {error}
                                        </div>
                                    ) : users.length === 0 ? (
                                        <div className="text-center py-4 text-muted">
                                            No users found. Create a new user to get started.
                                        </div>
                                    ) : (
                                        <>
                                            <div className="list-group">
                                                {users.map((user) => (
                                                    <div
                                                        key={user.id}
                                                        className={`list-group-item py-2 px-3 ${
                                                            selectedUser?.id === user.id ? 'selected' : ''
                                                        }`}
                                                        onClick={() => setSelectedUser(user)}
                                                    >
                                                        <div className="d-flex align-items-start">
                                                            <UserIcon size={18} className="text-muted me-2 mt-1"/>
                                                            <div className="flex-grow-1">
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <div>
                                                                        <h6 className="mb-0 text-truncate">{user.fullName || 'No name'}</h6>
                                                                        <p className="text-muted small mb-0">
                                                                            {user.email}
                                                                        </p>
                                                                    </div>
                                                                    <Badge
                                                                        className={`ms-2 px-2 py-1 text-xs ${
                                                                            user.status === 'active'
                                                                                ? 'bg-success-subtle text-success-emphasis'
                                                                                : 'bg-danger-subtle text-danger-emphasis'
                                                                        }`}
                                                                    >
                                                                        {user.status}
                                                                    </Badge>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="d-flex justify-content-between align-items-center mt-3">
                                                <div className="text-muted small">
                                                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                                                </div>
                                                {renderPagination()}
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="col-md-4">
                                    <Card className="border shadow-sm">
                                        <Card.Header className="border-bottom px-3 py-2">
                                            <h6 className="mb-0">Actions</h6>
                                        </Card.Header>
                                        <Card.Body className="p-2">
                                            <div className="d-grid gap-1">
                                                <button
                                                    className="w-100 d-flex align-items-center justify-content-center gap-2 px-3 py-1 border rounded text-sm fw-medium btn btn-outline-secondary text-dark action-button"
                                                    disabled={!selectedUser}
                                                    onClick={() => selectedUser && handleOpenEditModal(selectedUser)}
                                                >
                                                    <Edit size={16}/>
                                                    Edit
                                                </button>
                                                <button
                                                    className="w-100 d-flex align-items-center justify-content-center gap-2 px-3 py-1 border rounded text-sm fw-medium btn btn-outline-secondary text-dark action-button"
                                                    disabled={!selectedUser}
                                                    onClick={() => selectedUser && handleDeleteUser(selectedUser.id)}
                                                >
                                                    <Trash2 size={16}/>
                                                    Delete
                                                </button>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                    )}
                </div>

                <UserModal
                    isOpen={isModalOpen}
                    onClose={() => handleCloseModal(false)} // Not forced - will check for changes
                    formData={formData}
                    setFormData={setFormData}
                    selectedUser={selectedUser}
                    groups={groups}
                    onSubmit={handleSubmit}
                    isLoading={modalState.isLoading}
                    organizationId={currentUser.organizationId}
                    organizationDomain={currentUser.organizationDomain || ''}
                    emailError={emailError}
                />
            </div>
        </>
    );
};

export default UsersPage;